import { useState, useEffect } from "react";
import Navigation from "../components/Navigation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { FileText, TrendingUp, Target, MessageSquare, Users, BookOpen, Lightbulb } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const menuItems = [
  { icon: FileText, label: "Mental Training Plan", path: "/plans" },
  { icon: TrendingUp, label: "Mental Performance", path: "/performance" },
  { icon: Target, label: "Goals", path: "/goals" },
  { icon: MessageSquare, label: "AI Coach", path: "/ai-coach" },
  { icon: Users, label: "Teams", path: "/teams" }
];

export default function MistakeManagement({ user, onLogout }) {
  const [mistakes, setMistakes] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const [selectedMistake, setSelectedMistake] = useState(null);
  const [formData, setFormData] = useState({
    situation: "",
    mistake_description: "",
    emotional_response: "",
    learned_lesson: "",
    action_plan: ""
  });

  useEffect(() => {
    fetchMistakes();
  }, []);

  const fetchMistakes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/mistakes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMistakes(response.data);
    } catch (error) {
      toast.error("Failed to load mistake logs");
    }
  };

  const handleSubmit = async () => {
    if (!formData.situation || !formData.mistake_description || !formData.learned_lesson) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/mistakes`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Mistake logged! Great job reflecting and learning.");
      setShowLog(false);
      setFormData({
        situation: "",
        mistake_description: "",
        emotional_response: "",
        learned_lesson: "",
        action_plan: ""
      });
      fetchMistakes();
    } catch (error) {
      toast.error("Failed to log mistake");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-100">
      <Navigation user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex justify-between items-start mb-12">
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Mistake Management
            </h1>
            <p className="text-lg text-gray-600">
              Transform mistakes into learning opportunities
            </p>
          </div>
          <Button
            data-testid="log-mistake-btn"
            onClick={() => setShowLog(true)}
            className="bg-sky-600 hover:bg-sky-700"
          >
            Log a Mistake
          </Button>
        </div>

        {/* Info Card */}
        <Card className="border-0 shadow-lg bg-gradient-to-r from-indigo-50 to-sky-50 mb-8">
          <CardHeader>
            <div className="flex items-start space-x-3">
              <Lightbulb className="w-6 h-6 text-indigo-600 mt-1" />
              <div>
                <CardTitle className="text-xl text-indigo-900">Why Log Mistakes?</CardTitle>
                <CardDescription className="text-indigo-700 text-base">
                  Elite athletes see mistakes as data, not failures. By reflecting on what went wrong,
                  you extract lessons, build resilience, and prevent repeating errors. This journal
                  helps you bounce back stronger every time.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Mistake Logs */}
        <div className="space-y-6">
          {mistakes.map((mistake) => (
            <Card
              key={mistake.id}
              data-testid={`mistake-log-${mistake.id}`}
              onClick={() => setSelectedMistake(mistake)}
              className="border-0 shadow-lg bg-white/90 backdrop-blur-sm hover:shadow-xl transition-all duration-300 cursor-pointer"
            >
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <BookOpen className="w-5 h-5 text-sky-600" />
                      <CardTitle className="text-lg">{mistake.situation}</CardTitle>
                    </div>
                    <CardDescription className="text-base line-clamp-2">
                      {mistake.mistake_description}
                    </CardDescription>
                  </div>
                  <span className="text-sm text-gray-500">
                    {new Date(mistake.date).toLocaleDateString()}
                  </span>
                </div>
                {mistake.learned_lesson && (
                  <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm font-semibold text-green-900 mb-1">💡 Lesson Learned:</p>
                    <p className="text-sm text-green-800">{mistake.learned_lesson}</p>
                  </div>
                )}
              </CardHeader>
            </Card>
          ))}

          {mistakes.length === 0 && (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">
                No mistakes logged yet. Remember, mistakes are stepping stones to mastery!
              </p>
              <Button
                data-testid="log-first-mistake-btn"
                onClick={() => setShowLog(true)}
                className="bg-sky-600 hover:bg-sky-700"
              >
                Log Your First Mistake
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Log Mistake Dialog */}
      <Dialog open={showLog} onOpenChange={setShowLog}>
        <DialogContent data-testid="log-mistake-dialog" className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">Log a Mistake</DialogTitle>
            <DialogDescription>
              Reflect on what happened and extract the learning
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="situation">Situation / Context *</Label>
              <Input
                data-testid="situation-input"
                id="situation"
                placeholder="e.g., Championship game, 4th quarter"
                value={formData.situation}
                onChange={(e) => setFormData({ ...formData, situation: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="mistake">What happened? (The Mistake) *</Label>
              <Textarea
                data-testid="mistake-description-textarea"
                id="mistake"
                placeholder="Describe objectively what went wrong..."
                value={formData.mistake_description}
                onChange={(e) => setFormData({ ...formData, mistake_description: e.target.value })}
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="emotional">How did you feel?</Label>
              <Textarea
                data-testid="emotional-response-textarea"
                id="emotional"
                placeholder="Frustrated, disappointed, angry, embarrassed..."
                value={formData.emotional_response}
                onChange={(e) => setFormData({ ...formData, emotional_response: e.target.value })}
                rows={2}
              />
            </div>
            <div>
              <Label htmlFor="lesson">What did you learn? *</Label>
              <Textarea
                data-testid="learned-lesson-textarea"
                id="lesson"
                placeholder="The insight or lesson from this experience..."
                value={formData.learned_lesson}
                onChange={(e) => setFormData({ ...formData, learned_lesson: e.target.value })}
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="action">Action Plan (What will you do differently?)</Label>
              <Textarea
                data-testid="action-plan-textarea"
                id="action"
                placeholder="Specific steps to prevent this from happening again..."
                value={formData.action_plan}
                onChange={(e) => setFormData({ ...formData, action_plan: e.target.value })}
                rows={3}
              />
            </div>
            <Button
              data-testid="submit-mistake-log-btn"
              onClick={handleSubmit}
              className="w-full bg-sky-600 hover:bg-sky-700 text-lg py-6"
            >
              Save Reflection
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Mistake Detail Dialog */}
      <Dialog open={!!selectedMistake} onOpenChange={() => setSelectedMistake(null)}>
        <DialogContent data-testid="view-mistake-dialog" className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">{selectedMistake?.situation}</DialogTitle>
            <DialogDescription>
              {new Date(selectedMistake?.date || new Date()).toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div>
              <h3 className="font-semibold text-lg mb-2">What Happened</h3>
              <p className="text-gray-700">{selectedMistake?.mistake_description}</p>
            </div>
            {selectedMistake?.emotional_response && (
              <div>
                <h3 className="font-semibold text-lg mb-2">Emotional Response</h3>
                <p className="text-gray-700">{selectedMistake.emotional_response}</p>
              </div>
            )}
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <h3 className="font-semibold text-lg text-green-900 mb-2">💡 Lesson Learned</h3>
              <p className="text-green-800">{selectedMistake?.learned_lesson}</p>
            </div>
            {selectedMistake?.action_plan && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-lg text-blue-900 mb-2">🎯 Action Plan</h3>
                <p className="text-blue-800">{selectedMistake.action_plan}</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}